// Unpublished Work © 2022-2024 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import FormDialog from 'Ui/components/common/form-dialog/form-dialog';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import ValidationInput from 'Ui/components/common/form/validation-input';
import {useSave} from 'Ui/react-hooks/use-save';
import {createCourseNameToIdMap} from 'Services/vendor-service';
import {closeDialog as closeReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {translateErrorMessage} from 'Utils/error-utils';

function CourseNameDialog(props) {
    const {
        closeDialog,
        invalidInputs,
        setValid,
        translations,
        updateCourseNames
    } = props;

    const {addToast} = useToast();

    const [courseName, setCourseName] = React.useState('');
    const [saveFunc, disableSave, isSaving] = useSave(async () => {
        try {
            const {courseNameToIdMap} = await createCourseNameToIdMap({
                courseName: courseName.toLowerCase(),
                dataSource: 'pogo'
            });

            updateCourseNames(courseNameToIdMap.courseName);

            closeDialog();
        } catch (e) {
            const errorMessage = translateErrorMessage(e.response.data.message, translations);

            addToast({
                message: errorMessage,
                type: TOAST_TYPE.ERROR
            });
        }
    }, {
        invalidInputs
    });

    return (
        <FormDialog
            cancelLabel={translations.CANCEL}
            className='course-name-dialog'
            closeHandler={closeDialog}
            disableSave={disableSave}
            footerLoading={isSaving}
            onSave={saveFunc}
            saveLabel={translations.save}
            title={translations.ONLINK_COURSE_NAME}
            translations={translations}
        >
            <form
                className='settings-body'
                onSubmit={saveFunc}
            >
                <ValidationInput
                    debounceTimeout={window.props.debounceTimeout}
                    errors={{
                        valueMissing: translations.REQUIRED_FIELD_TEXT
                    }}
                    label={translations.ONLINK_COURSE_NAME}
                    name='courseName'
                    onChange={(event) => setCourseName(event.target.value)}
                    required={true}
                    setValid={setValid}
                    tabIndex={0}
                    type='text'
                    value={courseName}
                />
            </form>
        </FormDialog>
    );
}

CourseNameDialog.propTypes = {
    closeDialog: PropTypes.func,
    invalidInputs: PropTypes.instanceOf(Set),
    setValid: PropTypes.func,
    translations: PropTypes.translations,
    updateCourseNames: PropTypes.func
};

export function mapStateToProps(state) {
    return {
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeDialog() {
            dispatch(closeReduxDialog(dialogTypes.COURSE_NAME_DIALOG));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(FormValidator(CourseNameDialog));
